import React, { useEffect, useRef, useState } from "react";
import { handlePrintData } from "../../../utils/PrintData";
import { PrintIcon } from "../../../utils/TableActionIcons";
import ReportHeader from "../../../shared/ReportHeader";
import ReportFooter from "../../../shared/ReportFooter";
import { formatDate } from "../../../utils/FormateDateTime";
import { fetchData } from "../../../helpers/fetchData";
import CheckPermission from "../../../hooks/CheckPermission";

const isRenderableImageSource = (value) =>
  typeof value === "string" &&
  (value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:"));

const usePresignedImageSource = (objectKeyOrUrl) => {
  const [src, setSrc] = useState("");

  useEffect(() => {
    let isMounted = true;

    const resolveSource = async () => {
      if (!objectKeyOrUrl) {
        setSrc("");
        return;
      }
      if (isRenderableImageSource(objectKeyOrUrl)) {
        setSrc(objectKeyOrUrl);
        return;
      }

      try {
        const response = await fetchData(
          "POST",
          "/v3/files/presigned-url?expiry=168",
          {
            objectKey: objectKeyOrUrl,
          },
        );
        if (isMounted) {
          setSrc(response?.presignedURL || "");
        }
      } catch (error) {
        if (isMounted) {
          setSrc("");
        }
      }
    };

    resolveSource();

    return () => {
      isMounted = false;
    };
  }, [objectKeyOrUrl]);

  return src;
};

const ValueLine = ({
  label,
  ar,
  value,
  className = "",
  labelClassName = "w-[142px]",
  arClassName = "w-[126px]",
}) => (
  <div
    className={`grid min-h-[27px] grid-cols-[auto_1fr_auto] items-center gap-2 border-b border-dotted border-gray-400 ${className}`}
  >
    <b
      className={`${labelClassName} shrink-0 whitespace-nowrap text-[12px] leading-5`}
    >
      {label} :
    </b>
    <span className="min-w-0 px-1 font-semibold leading-5 break-words">
      {value || ""}
    </span>
    {ar && (
      <b
        className={`${arClassName} shrink-0 whitespace-nowrap text-right text-[12px] leading-5`}
        dir="rtl"
      >
        : {ar}
      </b>
    )}
  </div>
);

const VitalLine = ({ label, ar, value }) => (
  <div className="grid grid-cols-[48px_1fr_56px] items-center gap-1">
    <b className="whitespace-nowrap text-[12px] leading-5">{label}:</b>
    <span className="min-w-0 border-b border-dotted border-gray-500 px-1 text-center font-semibold leading-5">
      {value || ""}
    </span>
    <b className="whitespace-nowrap text-right text-[12px] leading-5" dir="rtl">
      : {ar}
    </b>
  </div>
);

const TextBlockLine = ({
  label,
  ar,
  value,
  minHeight = "min-h-[44px]",
  wideLabel = false,
}) => (
  <div
    className={`grid overflow-hidden border border-gray-400 ${wideLabel ? "grid-cols-[215px_1fr_165px]" : "grid-cols-[155px_1fr_135px]"}`}
  >
    <b className="border-r border-gray-400 bg-gray-50 px-2 py-1 text-[12px] leading-5">
      {label} :
    </b>
    <div
      className={`${minHeight} px-2 py-1 font-semibold leading-5 whitespace-pre-wrap break-words`}
    >
      {value || ""}
    </div>
    <b
      className="border-l border-gray-400 bg-gray-50 px-2 py-1 text-right text-[12px] leading-5"
      dir="rtl"
    >
      : {ar}
    </b>
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="border-b border-gray-600 bg-gray-100 py-1.5 text-center text-[14px] font-semibold leading-tight">
    {children}
  </div>
);

const Check = ({ label, ar, checked }) => (
  <div className="grid grid-cols-[1fr_22px_1fr] items-center gap-2 text-[13px]">
    <span className="font-semibold">{label}</span>
    <span className="inline-flex h-[18px] w-[18px] items-center justify-center border border-gray-800 text-[12px] font-semibold">
      {checked ? "✓" : ""}
    </span>
    <span className="text-right font-semibold" dir="rtl">
      {ar}
    </span>
  </div>
);

const ImageBox = ({
  title,
  src,
  imageClassName = "max-h-[64px] max-w-[145px]",
}) => (
  <div className="flex min-h-[86px] flex-col items-center justify-end">
    {src ? (
      <img
        src={src}
        alt={title}
        className={`${imageClassName} object-contain`}
      />
    ) : (
      <div className="h-[64px] w-full" />
    )}
    <p className="mt-1 w-full border-t border-dotted border-gray-600 pt-1 text-center text-[11px] font-semibold">
      {title}
    </p>
  </div>
);

function ViewReferralFormReport({ selectedReferralData }) {
  const { hasPermission } = CheckPermission();
  const printRef = useRef(null);
  const data = selectedReferralData || {};
  const report = data?.hospital_report || {};
  const vitals = data?.vitals || {};
  const practitioner = data?.practitioner || {};
  const signatureSrc = usePresignedImageSource(practitioner?.signature);
  const stampSrc = usePresignedImageSource(practitioner?.stamp);

  return (
    <div
      ref={printRef}
      className="mx-auto flex min-h-screen max-w-[980px] flex-col bg-white text-[13px] text-gray-950"
    >
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 6mm 8mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .referral-print-root { max-width: none !important; font-size: 12px !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
      <div
        className="absolute top-8 right-20 cursor-pointer print:hidden"
        onClick={() =>
          handlePrintData(printRef?.current?.innerHTML, "ReferralForm")
        }
      >
        <PrintIcon iconSize={20} />
      </div>
      <ReportHeader reportName="Referral Form - بطاقة تحويل واستشارة" />

      <div className="referral-print-root overflow-hidden border border-gray-600">
        <div className="grid grid-cols-[1fr_260px_1fr] items-center border-b border-gray-600 px-3 py-2">
          <ValueLine
            label="Date"
            ar="التاريخ"
            value={formatDate(data?.visit_date || data?.created_at)}
            labelClassName="w-[52px]"
            arClassName="w-[64px]"
          />
          <div className="text-center text-[17px] leading-tight font-semibold ">
            [ بطاقة تحويل واستشارة ]<br />
            REFERRAL FORM
          </div>
          <div className="text-right text-[12px] font-semibold" dir="rtl">
            المملكة العربية السعودية
            <br />
            وزارة الصحة
          </div>
        </div>

        <div className="space-y-1.5 p-3">
          <ValueLine
            label="To Hospital"
            ar="السيد مدير مستشفى"
            value={data?.to_hospital}
          />
          <ValueLine label="Department" ar="القسم" value={data?.department} />
          <div className="grid grid-cols-2 gap-x-5">
            <ValueLine
              label="Patient Name"
              ar="اسم المريض"
              value={data?.patient?.full_name}
            />
            <ValueLine
              label="No Of Med. File"
              ar="رقم الملف الصحي"
              value={data?.patient?.file_no || data?.patient?.mrn}
            />
            <ValueLine label="Age" ar="العمر" value={data?.age} />
            <ValueLine
              label="Address"
              ar="العنوان"
              value={data?.address || data?.patient?.address}
            />
          </div>
          <div className="grid grid-cols-4 gap-4 border border-gray-500 bg-gray-50 px-2 py-1">
            <VitalLine label="B/P" ar="الضغط" value={vitals?.blood_pressure} />
            <VitalLine label="Temp." ar="الحرارة" value={vitals?.temperature} />
            <VitalLine
              label="Resp/R"
              ar="التنفس"
              value={vitals?.respiratory_rate}
            />
            <VitalLine label="Pulse/R" ar="النبض" value={vitals?.pulse_rate} />
          </div>
          <ValueLine
            label="Type of Referral"
            ar="نوع الإحالة"
            value={data?.type_of_referral}
          />
          <ValueLine
            label="Treating Dr."
            ar="الطبيب المعالج"
            value={data?.treating_doctor_name || practitioner?.name}
          />
          <TextBlockLine
            label="Complaint and Duration"
            ar="الشكوى ومدتها"
            value={data?.complaint_and_duration}
          />
          <TextBlockLine
            label="Physical Examination"
            ar="الفحص السريري"
            value={data?.physical_examination}
          />
          <TextBlockLine
            label="Investigations"
            ar="التحاليل والفحوصات"
            value={data?.investigations}
            minHeight="min-h-[34px]"
          />
          <TextBlockLine
            label="Provisional Diagnosis"
            ar="التشخيص المبدئي"
            value={data?.provisional_diagnosis}
            minHeight="min-h-[34px]"
          />
          <TextBlockLine
            label="Treatment Given"
            ar="العلاج المعطى"
            value={data?.treatment_given}
            minHeight="min-h-[34px]"
          />
          <TextBlockLine
            label="Reasons for Referral"
            ar="سبب الإحالة"
            value={data?.reasons_for_referral}
          />

          <div className="mt-3 grid grid-cols-[1fr_1fr] items-end gap-12 border-t border-gray-500 pt-2">
            <ImageBox
              title="Signature / التوقيع"
              src={signatureSrc}
              imageClassName="max-h-[58px] max-w-[170px]"
            />
            <ImageBox
              title="Stamp / الختم"
              src={stampSrc}
              imageClassName="max-h-[68px] max-w-[120px]"
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden border border-t-0 border-gray-600">
        <SectionTitle>
          تقرير المستشفى
          <br />
          Hospital Report
        </SectionTitle>
        <div className="space-y-1.5 p-3">
          <ValueLine
            label="Hospital File No"
            ar="رقم الملف بالمستشفى"
            value={report?.hospital_file_number}
          />
          <TextBlockLine
            label="Findings and Diagnosis"
            ar="نتيجة الاستشارة والتشخيص"
            value={report?.findings_and_diagnosis}
            minHeight="min-h-[52px]"
            wideLabel
          />
          <TextBlockLine
            label="Treatment and Recommendations"
            ar="العلاج والتوصيات"
            value={report?.treatment_and_recommendations}
            minHeight="min-h-[52px]"
            wideLabel
          />
          <div className="ml-auto grid max-w-[430px] gap-2 border border-gray-500 bg-gray-50 p-3">
            <Check
              label="Discharge"
              ar="انتهاء الاستشارة"
              checked={report?.disposition === "Discharge"}
            />
            <Check
              label="Clinic Follow-UP"
              ar="مراجعة العيادة"
              checked={report?.disposition === "Clinic Follow-UP"}
            />
            <Check
              label="Hospital Admission"
              ar="دخول المستشفى"
              checked={report?.disposition === "Hospital Admission"}
            />
          </div>
        </div>
      </div>
      <div className="mt-auto">
        <ReportFooter />
      </div>
    </div>
  );
}

export default ViewReferralFormReport;
